import {useQuery} from "@apollo/client";
import classNames from "classnames";
import {useState} from "react";

import {ToggleButton} from "@/components/toggleButton/ToggleButton.tsx";
import {ToggleButtonItem} from "@/components/toggleButton/ToggleButton.types.ts";
import {
  GQL_PRIVATE_METRIC_DATAPOINTS_QUERY,
  GQL_PUBLIC_METRIC_DATAPOINTS_QUERY,
} from "@/lib/queries/statusPageConsumption.ts";

import type {PublicStatusPageMetric} from "../../../types.ts";
import {MetricChart, MetricTimeRange} from "./MetricChart";

import styles from "./MetricCard.module.scss";

const TIME_RANGE_ITEMS: ToggleButtonItem[] = [
  {id: "day", label: "Day"},
  {id: "week", label: "Week"},
  {id: "month", label: "Month"},
];

export interface MetricCardProps {
  metric: PublicStatusPageMetric;
  mode: "public" | "private";
  slug?: string;
  pageId?: number;
  timezone?: string;
  variant?: "default" | "inspire";
}

export const MetricCard = ({metric, mode, slug, pageId, timezone, variant = "default"}: MetricCardProps) => {
  const [timeRange, setTimeRange] = useState<MetricTimeRange>("day");
  const isPrivate = mode === "private";

  const {data: publicData, loading: publicLoading} = useQuery(GQL_PUBLIC_METRIC_DATAPOINTS_QUERY, {
    variables: {slug: slug ?? "", metricId: metric.id, timerange: timeRange},
    skip: isPrivate || !slug,
  });

  const {data: privateData, loading: privateLoading} = useQuery(GQL_PRIVATE_METRIC_DATAPOINTS_QUERY, {
    variables: {id: pageId ?? 0, metricId: metric.id, timerange: timeRange},
    skip: !isPrivate || !pageId,
  });

  const loading = isPrivate ? privateLoading : publicLoading;
  // A sample with no value is kept rather than dropped so the chart draws a gap there, as legacy
  // does — dropping it would join the two sides of an outage with a straight line.
  const rawDatapoints: {ts: string; value: number | null}[] =
    (isPrivate ? privateData?.privateMetricDatapoints : publicData?.publicMetricDatapoints)?.datapoints ?? [];

  const chartData: [number, number | null][] = rawDatapoints.map(({ts, value}) => [new Date(ts).getTime(), value]);

  return (
    <div className={classNames(styles.metricCard, {[styles.metricCardInspire]: variant === "inspire"})}>
      <div className={classNames(styles.metricHeader, {[styles.metricHeaderInspire]: variant === "inspire"})}>
        <span className={styles.metricName}>{metric.name}</span>
        <ToggleButton
          items={TIME_RANGE_ITEMS}
          selectedId={timeRange}
          onChange={(id) => setTimeRange(id as MetricTimeRange)}
          variant="segmentedDark"
          className={styles.toggle}
        />
      </div>
      <div className={styles.metricChartWrapper}>
        <MetricChart data={chartData} timezone={timezone} loading={loading} />
      </div>
    </div>
  );
};
